import { select } from '@ngrx/store';
import { Observable, zip } from 'rxjs';
import { map } from 'rxjs/operators';
import { PaymentHeader } from '@cemex-core/types-v1';
import { TranslationService } from '@cemex-core/angular-localization-v7';

import { AbstractPaymentEngine } from './abstract-payment-engine';
import { PaymentEngineConfiguration } from './payment-engine-configuration';
import { PaymentConfiguration } from './../models/payment-configuration';
import { PaymentRequest } from './../models/payment-request';
import { PayerBrokerRequest } from './../models/payer-broker-request';
import * as fromPayments from './../store/reducers/index';
import { StorageManagerService } from '@app/core/services/storage-manager.service';


export class PaymentEngineCO extends AbstractPaymentEngine {

    constructor(configuration: PaymentEngineConfiguration, t: TranslationService, private storageManagerService: StorageManagerService) {
        super(configuration, t);
    }

    getBanks(): any {
        throw new Error('Method not implemented.');
    }

    getPaymentConfiguration(): Observable<PaymentConfiguration> {
        return zip(
            super.initPaymentConfiguration(),
            this.bankService.getBankListV2({
                companyCode: this.company.companyCode,
                countryCode: this.customer.countryCode,
                customerCode: this.customer.customerCode
            })
        ).pipe(
            map(([paymentConfiguration, banks]) => {
                paymentConfiguration.paymentMethods.forEach(pm => {
                    pm.paymentNetworks = banks;
                    pm.displayName = pm.code.toLowerCase() === 'transfer'  ? 'PSE' : pm.description;

                    pm.displayDescription =  pm.code.toLowerCase() === 'transfer' ?
                        this.t.pt('views.regular_payments.transfer_method_description') :
                        pm.enableRestrictionHour ? this.t.pt('views.payment_methods.payment_method_restricted') + ' ' + pm.hourFrom + ' - ' + pm.hourTo + '.':
                        this.t.pt('views.regular_payments.credicard_method_description');
                    pm.displayIcon = 'selection-container__icon cmx-icon-module-invoices-payments';
                    pm.checked = pm.code.toLowerCase() === 'transfer' ? true : false;
                });
                return paymentConfiguration;
            })
        );
    }

    createReference(request: PaymentRequest): Observable<any> {
        return this.paymentService.createReferenceV3(request);
    }

    getPayerBroker(request: PayerBrokerRequest, brokerCode: string): Observable<any> {
        if (this.isExternalCompany(request.company.companyCode)) {
          return this.getPayerBrokerForConstruagil(request, brokerCode);
        }

        let paymentType = '';
        this.store.pipe(
            select(fromPayments.isAdvancePayment),
            map(isAdvancePayment => {
                if (isAdvancePayment && request.paymentType.paymentTypeCode === 'TR') {
                    paymentType = '113';
                } else if (isAdvancePayment && request.paymentType.paymentTypeCode === 'CC') {
                    paymentType = '1113';
                } else if (!isAdvancePayment && request.paymentType.paymentTypeCode === 'TR') {
                    paymentType = '1';
                } else if (!isAdvancePayment && request.paymentType.paymentTypeCode === 'CC') {
                    paymentType = '1001';
                }

                request.paymentType.paymentTypeCode = paymentType;
            })
        ).subscribe();

        const userProfile = this.storageManagerService.user_profile;
        const refreshToken = this.storageManagerService.refresh_token;
        const request2: PayerBrokerRequest = {
            country: request.country,
            customer: request.customer,
            referenceCode: request.referenceCode,
            amount: request.amount,
            company: request.company,
            paymentType: request.paymentType,
            user: { userAccount: userProfile.userAccount },
            urlRedirect: window.location.href.substring( 0, document.location.href.lastIndexOf( '/' ))
                + '/return?token=' + refreshToken + '&amp;c=' + request.country.countryCode
                + '&amp;b=PSE01' + '&amp;r=' + request.referenceCode
        };

        // return this.paymentService.getPayerBroker(request2, brokerCode, 'pe');
        return this.paymentService.getPayerBrokerV2( request2, brokerCode);
    }

    getPayerBrokerForConstruagil(request: PayerBrokerRequest, brokerCode: string): Observable<any> {
      let paymentType = '';
      this.store.pipe(
          select(fromPayments.isAdvancePayment),
          map(isAdvancePayment => {
              if (isAdvancePayment && request.paymentType.paymentTypeCode === 'TR') {
                  paymentType = '2002';
              } else if (isAdvancePayment && request.paymentType.paymentTypeCode === 'CC') {
                  paymentType = '2012';
              } else if (!isAdvancePayment && request.paymentType.paymentTypeCode === 'TR') {
                  paymentType = '2001';
              } else if (!isAdvancePayment && request.paymentType.paymentTypeCode === 'CC') {
                  paymentType = '2011';
              }

              request.paymentType.paymentTypeCode = paymentType;
          })
      ).subscribe();

      const userProfile = this.storageManagerService.user_profile;
      const refreshToken = this.storageManagerService.refresh_token;

      const request2 = {
          countryCode: request.country.countryCode,
          customerCode: request.customer.customerCode,
          referenceCode: request.referenceCode,
          amount: request.amount,
          companyCode: request.company.companyCode,
          paymentType: request.paymentType.paymentTypeCode,
          customerDesc: request.customer.customerDesc,
          userAccount: userProfile.userAccount,
          urlRedirect: window.location.href.substring( 0, document.location.href.lastIndexOf( '/' ))
                + '/return?token=' + refreshToken + '&amp;c=' + request.country.countryCode
                + '&amp;b=PSE01' + '&amp;r=' + request.referenceCode
      };

      if (request.company.applicationCode) {
        request2.urlRedirect += '&amp;s=' + request.company.applicationCode;
      }

      if (request.company.returnUrl) {
        request2.urlRedirect += '&amp;g=' + request.company.returnUrl;
      }

      return this.paymentService.getPayerBroker(request2, brokerCode, 'pe');
  }

    callToNetworking(totalToPay: string, referenceCode: string, payerInformation: any): void {
        const action = payerInformation.urlRedirect;
        const parameters = {
            returnCode: payerInformation.returnCode,
            ticketId: payerInformation.ticketId,
        };

        this.createFormRequestNetwork(action, 'POST', '_self', parameters);
    }

    isExternalCompany(companyCode: string): boolean {
      return this.filterExternalCompaniesFeature.companies.some(company => company === companyCode);
    }


    saveExternalOrderRelation(externalOrder: string, reference: string, bankCode: string, countryCode: string, customerCode: string, companyCode: string): Observable<any> {
        return this.paymentService.saveExternalOrderRelation(externalOrder, reference, bankCode, countryCode, customerCode, companyCode);
    }

    getHeaderPayerInformation(request: any, payerInformationResponse: any, storeDataInfo: any): any {
        const paymentHeader = request.header;
        const response = payerInformationResponse;
        let psePaymentType = paymentHeader.paymentMethod.toLowerCase() === 'pse' ? '113'
                        : paymentHeader.paymentMethod.toLowerCase() === 'credit' ? '1113' : '';
        let payerObj = {
            countryCode: paymentHeader.countryCompany,
            customerCode: paymentHeader.customerNo1,
            referenceCode: response.idTransaction,
            amount: paymentHeader.amountLocalCurrency,
            companyCode: paymentHeader.companyCode,
            paymentType: psePaymentType,
            customerDesc: paymentHeader.client,
            userAccount: storeDataInfo.UserAccount,
        urlRedirect: window.location.href.substring(0, document.location.href.lastIndexOf( '/' ))
            + '/return?token=' + storeDataInfo.Token + '&amp;c=' + paymentHeader.countryCompany + '&amp;b=PSE01'  + '&amp;r=' + response.idTransaction,
            externalOrder: ''
        };

        if (storeDataInfo.IsExternalAppAndCompany) {
            payerObj.externalOrder = storeDataInfo.ExternalOrder;

            const auxSourceApp = '/' + storeDataInfo.PaymentExternalData.sourceApp;
            let auxHref = document.location.href;

            if (document.location.href.includes(auxSourceApp)) {
              auxHref = auxHref.replace(auxSourceApp, '');
            }

            auxHref = auxHref.substring(0, auxHref.lastIndexOf( '/' ));

            payerObj.urlRedirect = auxHref + '/return?token=' + storeDataInfo.Token + '&amp;c=' + paymentHeader.countryCompany
              + '&amp;b=PSE01'  + '&amp;r=' + response.idTransaction;

            if (document.location.href.includes(auxSourceApp)) {
              payerObj.urlRedirect += '&amp;s=' + storeDataInfo.PaymentExternalData.sourceApp;
            }

            if (storeDataInfo.PaymentExternalData.returnUrl) {
              payerObj.urlRedirect += '&amp;t=' + storeDataInfo.PaymentExternalData.returnUrl;
            }
          }

        return payerObj;
    }

}
